import { useState } from 'react';
import { Box, Link, Stack, Typography } from '@mui/material';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import CustomButton from '../../../components/CustomButton';
import ThemedTextField from '../../../components/ThemedTextField';
import { useTenant } from '../../../contexts/TenantContext';
import { formatPrice } from '../../../utils/priceUtils';
import useCheckCoupon from '../api/useCheckCoupon';

const discountLabel = (coupon) => (coupon.discountType === 'PERCENT'
  ? `−${coupon.discountValue}%`
  : `−${formatPrice(coupon.discountValue)}`);

/**
 * The coupon on the confirmation page. Folded into a link until the guest asks for it, so a guest
 * without a code is not made to feel they are missing one. A coupon the backend refuses is told
 * under the field; one it accepts replaces the field with its code and a way to take it off. The
 * price itself shows in the supply summary above.
 *
 * The check is a preview: "Véglegesítés" sends the code, and the confirmation checks it again.
 */
const CouponField = ({ supplyId, appliedCoupon, onApply, onRemove }) => {
  const { displayConfig } = useTenant();
  const { checkCoupon, loading } = useCheckCoupon();
  const [isOpen, setIsOpen] = useState(false);
  const [code, setCode] = useState('');
  const [errorMessage, setErrorMessage] = useState(null);
  const accent = displayConfig?.primaryColor;

  const linkSx = { color: accent, fontWeight: 600, cursor: 'pointer', textDecorationColor: accent, fontSize: 14 };

  // Taken off by the guest, the coupon folds back into the link: nothing left to wonder about.
  const remove = () => {
    setIsOpen(false);
    setCode('');
    onRemove();
  };

  if (appliedCoupon) {
    return (
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap data-testid="coupon-applied">
        <LocalOfferOutlinedIcon sx={{ fontSize: 18, color: accent }} />
        <Typography fontSize={14} color={displayConfig?.color}>
          Kupon: <Box component="strong">{appliedCoupon.couponCode}</Box> ({discountLabel(appliedCoupon)})
        </Typography>
        <Link component="button" type="button" onClick={remove} sx={linkSx} data-testid="coupon-remove">
          Eltávolítás
        </Link>
      </Stack>
    );
  }

  if (!isOpen) {
    return (
      <Box>
        <Link component="button" type="button" onClick={() => setIsOpen(true)} sx={linkSx} data-testid="coupon-open">
          Van kuponkódod?
        </Link>
      </Box>
    );
  }

  const apply = async (event) => {
    event.preventDefault();
    if (!code.trim()) return;

    const result = await checkCoupon(code.trim(), supplyId);
    if (result.coupon) {
      setErrorMessage(null);
      onApply(result.coupon);
    } else if (result.errorMessage) {
      setErrorMessage(result.errorMessage);
    }
  };

  return (
    <Box component="form" onSubmit={apply} noValidate>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'flex-start' }}>
        <ThemedTextField
          label="Kuponkód"
          value={code}
          onChange={(event) => {
            setCode(event.target.value.toUpperCase());
            setErrorMessage(null);
          }}
          error={Boolean(errorMessage)}
          helperText={errorMessage}
          autoFocus
          inputProps={{ 'data-testid': 'coupon-input', autoCapitalize: 'characters', autoComplete: 'off' }}
          sx={{
            width: '100%',
            flex: 1,
            // A dark theme greys every helper text; an error has to stay red.
            '& .MuiFormHelperText-root.Mui-error': { color: 'error.main' },
          }}
        />
        <Box sx={{ pt: { sm: 1 } }}>
          <CustomButton text="Beváltás" type="submit" isLoading={loading} textTransform="none" />
        </Box>
      </Stack>
    </Box>
  );
};

export default CouponField;
